import React, { useEffect, useState, useRef } from 'react';
import axios from 'axios';
import RevolutCheckout from '@revolut/checkout';
import { Zap, Loader2, AlertCircle, Check, X, CreditCard } from 'lucide-react';
import { useApp } from '../context/AppContext';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

/**
 * Auto top-up control for a single eSIM order on the dashboard.
 * Charges when usage >= 95% OR within 12h of expiry.
 */
export const AutoTopUpControl = ({ sim }) => {
  const { user, currentCurrency } = useApp();
  const [config, setConfig] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [pm, setPm] = useState(null);
  const [error, setError] = useState('');
  const [packages, setPackages] = useState([]);
  const [selectedPkgId, setSelectedPkgId] = useState('');
  const [showSetup, setShowSetup] = useState(false);
  // eSIM-style top-up selector state (type tabs + expiry slider)
  const [pickType, setPickType] = useState('fixed');
  const [pickDurIdx, setPickDurIdx] = useState(0);

  // Card-on-file setup flow (for users who paid with wallet pay)
  const [showCardSetup, setShowCardSetup] = useState(false);
  const [setupToken, setSetupToken] = useState(null);
  const [setupOrderId, setSetupOrderId] = useState(null);
  const [setupSaving, setSetupSaving] = useState(false);
  const [setupCardholder, setSetupCardholder] = useState('');
  const setupCardFieldRef = useRef(null);
  const setupCardInstanceRef = useRef(null);
  const [setupCardMounted, setSetupCardMounted] = useState(false);

  const orderId = sim.id;
  const iccid = sim.iccid;

  // Load auto top-up config + saved payment method
  const load = async () => {
    try {
      setLoading(true);
      const [cfgRes, pmRes] = await Promise.all([
        axios.get(`${API}/auto-topup/${orderId}`),
        user?.email
          ? axios.get(`${API}/payment-method/${encodeURIComponent(user.email)}`)
          : Promise.resolve({ data: { has_payment_method: false } }),
      ]);
      if (cfgRes.data?.success) {
        setConfig(cfgRes.data);
        setSelectedPkgId(cfgRes.data.package_id || sim.packageId || '');
      }
      if (pmRes.data?.success) setPm(pmRes.data);
    } catch (e) {
      console.error('Auto top-up load error:', e?.message);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, [orderId, user?.email]); // eslint-disable-line

  // Lazy-load top-up packages (Roamify's topup-specific list, scoped to this ICCID)
  const packagesLoadedRef = useRef(false);
  useEffect(() => {
    if (!showSetup || !iccid || packagesLoadedRef.current) return;
    let cancelled = false;
    packagesLoadedRef.current = true;
    setError('');
    (async () => {
      try {
        const resp = await axios.get(`${API}/esim/topup/packages/${iccid}`);
        // Backend always returns { success, data: { packages: [...] }, packages: [...] }
        const list = resp.data?.packages || resp.data?.data?.packages || [];
        if (!cancelled && Array.isArray(list)) {
          const normalised = list.map((p) => {
            const pid = p.packageId || p.package_id || p.id;
            const dataGb = typeof p.data_gb === 'number' ? p.data_gb : (parseFloat(p.dataAmount || p.data_amount || p.data || 0) || 0);
            const days = p.duration_days || parseInt(p.duration || p.duration_days || p.days || 0, 10) || 0;
            const price = parseFloat(p.retailPrice || p.retail_price || p.price || p.cost || 0) || 0;
            const type = p.type || 'fixed';
            const nameParts = [];
            if (type === 'unlimited') nameParts.push('Unlimited');
            else if (dataGb) nameParts.push(`${dataGb} GB`);
            if (days) nameParts.push(`${days} Days`);
            return {
              packageId: pid,
              name: p.package || p.name || nameParts.join(' - ') || pid,
              price,
              dataAmount: dataGb,
              duration: days,
              type,
            };
          }).filter(p => p.packageId);
          setPackages(normalised);
          if (normalised.length === 0) {
            setError('No top-up packages are currently available for this eSIM. Please try again later.');
          }
        }
      } catch (e) {
        if (!cancelled) {
          console.error('Top-up packages load error:', e?.message);
          packagesLoadedRef.current = false;
          setError('Could not load top-up packages. Please try again.');
        }
      }
    })();
    return () => { cancelled = true; };
  }, [showSetup, iccid]);

  // Keep a valid default selection for the current type + duration in the popup.
  useEffect(() => {
    if (!showSetup || packages.length === 0) return;
    const availTypes = { fixed: false, unlimited: false, voice: false };
    packages.forEach((p) => { availTypes[p.type] = true; });
    const eType = availTypes[pickType] ? pickType : (['fixed', 'unlimited', 'voice'].find((t) => availTypes[t]) || 'fixed');
    const durs = [...new Set(packages.filter((p) => p.type === eType).map((p) => p.duration))].sort((a, b) => a - b);
    const eDur = durs[Math.min(pickDurIdx, Math.max(0, durs.length - 1))] ?? durs[0];
    const opts = packages.filter((p) => p.type === eType && p.duration === eDur).sort((a, b) => a.dataAmount - b.dataAmount);
    if (opts.length && !opts.some((p) => p.packageId === selectedPkgId)) {
      setSelectedPkgId(opts[0].packageId);
    }
  }, [showSetup, packages, pickType, pickDurIdx]); // eslint-disable-line react-hooks/exhaustive-deps

  const handleEnable = async () => {
    setError('');
    // Make sure top-up packages are loaded so the default can match by data amount.
    if (packages.length === 0) {
      setError('Please wait — top-up packages are still loading.');
      return;
    }

    // Resolve the target package: explicit selection wins, otherwise pick the one whose
    // data amount matches the originally-purchased plan (so "Same as original" actually
    // maps to a real Roamify top-up package, not the original purchase id).
    let chosen = packages.find(p => p.packageId === selectedPkgId);
    if (!chosen && !selectedPkgId) {
      const targetGb = parseFloat(sim.dataAmount || sim.planAmount || 0) || 0;
      if (targetGb > 0) {
        chosen = packages
          .slice()
          .sort((a, b) => Math.abs(a.dataAmount - targetGb) - Math.abs(b.dataAmount - targetGb))[0];
      }
      if (!chosen) chosen = packages[0];
    }

    if (!chosen) {
      setError('No top-up packages available for this eSIM.');
      return;
    }

    setSaving(true);
    try {
      const resp = await axios.post(`${API}/auto-topup/${orderId}`, {
        enabled: true,
        package_id: chosen.packageId,
        package_name: chosen.name,
        package_amount: chosen.price,
        package_currency: 'AUD',
        display_currency: currentCurrency?.code || 'AUD',
        package_data_amount: chosen.dataAmount,
        package_duration_days: chosen.duration,
      });
      if (resp.data?.success) {
        setShowSetup(false);
        await load();
      } else {
        setError('Failed to enable auto top-up');
      }
    } catch (e) {
      setError(e.response?.data?.detail || 'Failed to enable auto top-up');
    } finally {
      setSaving(false);
    }
  };

  const handleDisable = async () => {
    setError('');
    setSaving(true);
    try {
      await axios.post(`${API}/auto-topup/${orderId}`, { enabled: false });
      await load();
    } catch (e) {
      setError(e.response?.data?.detail || 'Failed to disable');
    } finally {
      setSaving(false);
    }
  };

  const handleTriggerNow = async () => {
    setError('');
    setSaving(true);
    try {
      const resp = await axios.post(`${API}/auto-topup/${orderId}/trigger`);
      if (resp.data?.success) {
        // Success — refresh and clear error.
        setError('');
      } else {
        const stage = resp.data?.stage || 'unknown';
        const errMsg = resp.data?.error || resp.data?.last_error || 'Top-up could not be completed';
        setError(`Top-up failed (${stage}): ${errMsg}`);
      }
      await load();
    } catch (e) {
      setError(e.response?.data?.detail || 'Could not trigger top-up');
    } finally {
      setSaving(false);
    }
  };

  // Open card-on-file setup modal: create a $1 setup order then mount Revolut card field
  const handleOpenCardSetup = async () => {
    setError('');
    setSetupSaving(true);
    try {
      const resp = await axios.post(`${API}/revolut/setup-card-order`, {
        user_email: user.email,
      });
      if (resp.data?.success) {
        setSetupToken(resp.data.token);
        setSetupOrderId(resp.data.order_id);
        setShowCardSetup(true);
      } else {
        setError('Could not start card setup. Please try again.');
      }
    } catch (e) {
      setError(e.response?.data?.detail || 'Could not start card setup.');
    } finally {
      setSetupSaving(false);
    }
  };

  // Mount Revolut card field when setup modal opens
  useEffect(() => {
    if (!showCardSetup || !setupToken || !setupCardFieldRef.current) return;
    let mounted = true;
    if (setupCardInstanceRef.current?.destroy) {
      setupCardInstanceRef.current.destroy();
      setupCardInstanceRef.current = null;
      setSetupCardMounted(false);
    }
    (async () => {
      try {
        const instance = await RevolutCheckout(setupToken);
        if (!mounted) return;
        const cardField = instance.createCardField({
          target: setupCardFieldRef.current,
          onSuccess: async () => {
            try {
              const r = await axios.post(`${API}/revolut/setup-card-complete`, {
                revolut_order_id: setupOrderId,
                user_email: user.email,
              });
              if (r.data?.success) {
                setShowCardSetup(false);
                setSetupToken(null);
                setSetupOrderId(null);
                await load();
              } else {
                setError(r.data?.message || 'Card could not be saved.');
              }
            } catch (e) {
              setError(e.response?.data?.detail || 'Card setup failed.');
            }
          },
          onError: (err) => {
            setError(err?.message || 'Card setup failed.');
          },
          styles: {
            default: { color: '#1f2937', fontSize: '15px', '::placeholder': { color: '#9ca3af' } },
          },
        });
        setupCardInstanceRef.current = cardField;
        setSetupCardMounted(true);
      } catch (err) {
        console.error('Setup card field init error:', err);
        setError('Could not load card form. Please try again.');
      }
    })();
    return () => {
      mounted = false;
      if (setupCardInstanceRef.current?.destroy) setupCardInstanceRef.current.destroy();
      setupCardInstanceRef.current = null;
    };
    // eslint-disable-next-line
  }, [showCardSetup, setupToken]);

  const handleSubmitCardSetup = (e) => {
    e.preventDefault();
    if (!setupCardInstanceRef.current || setupSaving) return;
    setSetupSaving(true);
    setError('');
    setupCardInstanceRef.current.submit({
      name: setupCardholder.trim() || user?.firstName || 'Card Holder',
      email: user?.email || '',
      savePaymentMethodFor: 'merchant',
      billingAddress: { countryCode: 'AU', postcode: '2000' },
    });
    // setupSaving stays true; onSuccess/onError flips it.
    setTimeout(() => setSetupSaving(false), 8000);
  };


  if (loading) {
    return (
      <div className="border border-gray-200 dark:border-neutral-800 rounded-xl p-4 flex items-center gap-3 bg-gray-50 dark:bg-neutral-950">
        <Loader2 size={16} className="animate-spin text-gray-400 dark:text-neutral-500" />
        <span className="text-sm text-gray-500 dark:text-neutral-500">Loading auto top-up…</span>
      </div>
    );
  }

  const enabled = !!config?.enabled;
  const hasPm = !!pm?.has_payment_method;
  const status = config?.status || 'idle';

  // Derived selector data: available type buckets + durations for the picked type.
  const typeAvail = {
    fixed: packages.some((p) => p.type === 'fixed'),
    unlimited: packages.some((p) => p.type === 'unlimited'),
    voice: packages.some((p) => p.type === 'voice'),
  };
  const TYPE_TABS = [
    { key: 'fixed', label: 'Fixed GB' },
    { key: 'unlimited', label: 'Unlimited' },
    { key: 'voice', label: 'Calls & Text' },
  ].filter((t) => typeAvail[t.key]);
  const effType = typeAvail[pickType] ? pickType : (TYPE_TABS[0]?.key || 'fixed');
  const durations = [...new Set(packages.filter((p) => p.type === effType).map((p) => p.duration))].sort((a, b) => a - b);
  const effDur = durations[Math.min(pickDurIdx, Math.max(0, durations.length - 1))] ?? durations[0];
  const pickOptions = packages
    .filter((p) => p.type === effType && p.duration === effDur)
    .sort((a, b) => a.dataAmount - b.dataAmount);

  return (
    <div className="border border-gray-200 dark:border-neutral-800 rounded-xl bg-white dark:bg-neutral-900" data-testid={`auto-topup-${orderId}`}>
      <div className="flex items-center justify-between p-4">
        <div className="flex items-center gap-3">
          <div className={`w-10 h-10 rounded-xl flex items-center justify-center ${enabled ? 'bg-purple-100 text-purple-600' : 'bg-gray-100 text-gray-500'}`}>
            <Zap size={20} />
          </div>
          <div>
            <p className="font-bold text-gray-900 dark:text-neutral-100 text-sm">Auto Top-Up</p>
            <p className="text-xs text-gray-500 dark:text-neutral-500">
              {enabled
                ? `Will recharge at 95% data or 12h before expiry`
                : 'Stay connected — never run out of data'}
            </p>
          </div>
        </div>
        {enabled ? (
          <div className="flex items-center gap-2">
            <button
              onClick={handleTriggerNow}
              disabled={saving}
              className="px-3 py-2 text-xs font-bold text-white bg-purple-600 hover:bg-purple-700 rounded-lg disabled:opacity-50 flex items-center gap-1"
              data-testid={`auto-topup-trigger-${orderId}`}
              title="Run auto top-up now"
            >
              {saving ? <Loader2 size={12} className="animate-spin" /> : <Zap size={12} />}
              Top Up Now
            </button>
            <button
              onClick={handleDisable}
              disabled={saving}
              className="px-3 py-2 text-xs font-bold text-gray-700 dark:text-neutral-300 bg-gray-100 dark:bg-neutral-800 hover:bg-gray-200 dark:hover:bg-neutral-700 rounded-lg disabled:opacity-50"
              data-testid={`auto-topup-disable-${orderId}`}
            >
              {saving ? <Loader2 size={12} className="animate-spin" /> : 'Turn off'}
            </button>
          </div>
        ) : (
          <button
            onClick={() => {
              const next = !showSetup;
              if (next) {
                // Force a fresh fetch each time the setup panel opens.
                packagesLoadedRef.current = false;
                setPackages([]);
                setError('');
              }
              if (next || hasPm) setShowSetup(next);
              if (!hasPm && next) handleOpenCardSetup();
            }}
            disabled={setupSaving}
            className="px-4 py-2 text-xs font-bold rounded-lg bg-black text-white hover:bg-gray-800 disabled:opacity-50 dark:border dark:border-white"
            data-testid={`auto-topup-enable-${orderId}`}
            title={hasPm ? 'Enable auto top-up' : 'Add a card to enable auto top-up'}
          >
            {setupSaving ? <Loader2 size={14} className="animate-spin" /> : (hasPm ? 'Enable' : 'Add Card')}
          </button>
        )}
      </div>

      {enabled && (
        <div className="px-4 pb-4 flex flex-wrap items-center gap-2 text-xs">
          <span className={`inline-flex items-center gap-1 px-2 py-1 rounded-full font-semibold ${
            status === 'retrying' ? 'bg-amber-50 text-amber-700' :
            status === 'needs_review' ? 'bg-red-50 text-red-700' :
            'bg-emerald-50 text-emerald-700'
          }`}>
            {status === 'retrying' ? <Loader2 size={12} className="animate-spin" /> : <Check size={12} />}
            {status === 'retrying' ? `Retry ${config.retry_count}/3` : status === 'needs_review' ? 'Needs review' : 'Active'}
          </span>
          {config?.package_name && (
            <span className="text-gray-600 dark:text-neutral-400">Package: <strong className="text-gray-900 dark:text-neutral-100">{config.package_name}</strong></span>
          )}
          {pm?.payment_method?.card_last4 && (
            <span className="text-gray-500 dark:text-neutral-500 inline-flex items-center gap-1">
              <CreditCard size={12} /> •••• {pm.payment_method.card_last4}
            </span>
          )}
        </div>
      )}

      {!hasPm && !enabled && (
        <div className="mx-4 mb-4 p-3 bg-amber-50 dark:bg-amber-500/10 border border-amber-200 rounded-lg text-xs text-amber-800 dark:text-amber-300 flex items-start gap-2">
          <AlertCircle size={14} className="flex-shrink-0 mt-0.5" />
          <span>
            Auto top-up needs a saved card. Apple Pay / Google Pay can&apos;t be saved for future charges — click <strong>Add Card</strong> above to securely save a card (we&apos;ll charge $1 AUD, refunded as account credit).
          </span>
        </div>
      )}

      {error && (
        <div className="mx-4 mb-4 p-3 bg-red-50 dark:bg-red-500/10 border border-red-200 rounded-lg text-xs text-red-700 dark:text-red-300 flex items-start gap-2" data-testid={`auto-topup-error-${orderId}`}>
          <AlertCircle size={14} className="flex-shrink-0 mt-0.5" />
          <span>{error}</span>
        </div>
      )}

      {showCardSetup && !hasPm && (
        <div className="fixed inset-0 bg-black/50 z-50 flex items-end sm:items-center justify-center p-0 sm:p-4">
          <div className="bg-white dark:bg-neutral-900 w-full sm:max-w-md rounded-t-2xl sm:rounded-2xl p-6 max-h-[90vh] overflow-y-auto" data-testid="card-setup-modal">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-lg font-bold text-gray-900 dark:text-neutral-100">Save Card for Auto Top-Up</h3>
              <button
                type="button"
                onClick={() => { setShowCardSetup(false); setSetupToken(null); setError(''); }}
                className="text-gray-400 dark:text-neutral-500 hover:text-gray-700"
              >
                <X size={20} />
              </button>
            </div>
            <p className="text-sm text-gray-600 dark:text-neutral-400 mb-4">
              We&apos;ll charge <strong>$1 AUD</strong> now to securely save your card. The $1 is added to your account as credit and applied to your next top-up.
            </p>
            <form onSubmit={handleSubmitCardSetup} className="space-y-3">
              <input
                type="text"
                placeholder="Cardholder name"
                value={setupCardholder}
                onChange={(e) => setSetupCardholder(e.target.value)}
                className="w-full px-4 py-3 border border-gray-200 dark:border-neutral-800 rounded-xl focus:outline-none focus:border-gray-400 text-base"
                data-testid="card-setup-name"
              />
              <div className="border border-gray-200 dark:border-neutral-800 rounded-xl p-4 bg-white dark:bg-neutral-900 min-h-[60px]">
                <div ref={setupCardFieldRef} data-testid="card-setup-field" />
                {!setupCardMounted && (
                  <div className="flex items-center justify-center py-2">
                    <Loader2 className="w-4 h-4 animate-spin text-gray-400 dark:text-neutral-500" />
                    <span className="ml-2 text-xs text-gray-400 dark:text-neutral-500">Loading…</span>
                  </div>
                )}
              </div>
              {error && (
                <div className="text-xs text-red-600 dark:text-red-400 flex items-center gap-1"><AlertCircle size={12} /> {error}</div>
              )}
              <button
                type="submit"
                disabled={setupSaving || !setupCardMounted}
                className="w-full py-3 bg-black text-white rounded-xl font-bold text-sm hover:bg-gray-800 disabled:opacity-50 flex items-center justify-center gap-2"
                data-testid="card-setup-submit"
              >
                {setupSaving ? <Loader2 size={16} className="animate-spin" /> : <CreditCard size={16} />}
                {setupSaving ? 'Saving…' : 'Save Card & Charge $1 AUD'}
              </button>
              <p className="text-xs text-gray-500 dark:text-neutral-500 text-center">Secured by Revolut. Your card is never stored on our servers.</p>
            </form>
          </div>
        </div>
      )}

      {showSetup && !enabled && hasPm && (
        <div className="fixed inset-0 bg-black/50 z-50 flex items-end sm:items-center justify-center p-0 sm:p-4" data-testid={`auto-topup-selector-${orderId}`}>
          <div className="bg-white dark:bg-neutral-900 w-full sm:max-w-md rounded-t-2xl sm:rounded-2xl p-5 max-h-[90vh] overflow-y-auto">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-lg font-bold text-gray-900 dark:text-neutral-100">Choose your top-up plan</h3>
              <button
                type="button"
                onClick={() => { setShowSetup(false); setError(''); }}
                className="text-gray-400 dark:text-neutral-500 hover:text-gray-700"
                data-testid={`auto-topup-close-${orderId}`}
              >
                <X size={20} />
              </button>
            </div>

            {packages.length === 0 ? (
              <div className="flex items-center gap-2 text-gray-500 dark:text-neutral-400 text-sm py-6 justify-center">
                <Loader2 className="w-4 h-4 animate-spin" /> Loading top-up plans…
              </div>
            ) : (
              <>
                {/* Data-type tabs */}
                {TYPE_TABS.length > 1 && (
                  <div className="flex gap-2 mb-4">
                    {TYPE_TABS.map((t) => {
                      const active = effType === t.key;
                      return (
                        <button
                          key={t.key}
                          type="button"
                          onClick={() => { setPickType(t.key); setPickDurIdx(0); }}
                          className={`flex-1 px-3 py-2 rounded-xl text-xs font-bold border transition-colors ${
                            active
                              ? 'bg-black text-white border-black dark:bg-white dark:text-black dark:border-white'
                              : 'bg-white dark:bg-neutral-900 text-gray-600 dark:text-neutral-400 border-gray-200 dark:border-neutral-800'
                          }`}
                          data-testid={`auto-topup-type-${t.key}-${orderId}`}
                        >
                          {t.label}
                        </button>
                      );
                    })}
                  </div>
                )}

                {/* Expiry / duration slider */}
                {durations.length > 0 && (
                  <div className="mb-4">
                    <div className="flex items-center justify-between mb-1">
                      <label className="text-xs font-semibold text-gray-700 dark:text-neutral-300">Validity</label>
                      <span className="text-sm font-black text-gray-900 dark:text-neutral-100">{effDur} days</span>
                    </div>
                    <input
                      type="range"
                      min={0}
                      max={Math.max(0, durations.length - 1)}
                      step={1}
                      value={Math.min(pickDurIdx, durations.length - 1)}
                      onChange={(e) => setPickDurIdx(parseInt(e.target.value, 10))}
                      className="w-full accent-black dark:accent-white"
                      data-testid={`auto-topup-duration-${orderId}`}
                      disabled={durations.length <= 1}
                    />
                    <div className="flex justify-between text-[10px] text-gray-400 dark:text-neutral-600 mt-0.5">
                      <span>{durations[0]}d</span>
                      <span>{durations[durations.length - 1]}d</span>
                    </div>
                  </div>
                )}

                {/* Package options for the selected type + duration */}
                <div className="space-y-2 mb-4">
                  {pickOptions.map((p) => {
                    const sel = selectedPkgId === p.packageId;
                    return (
                      <button
                        key={p.packageId}
                        type="button"
                        onClick={() => setSelectedPkgId(p.packageId)}
                        className={`w-full flex items-center justify-between px-4 py-3 rounded-xl border text-left transition-colors ${
                          sel
                            ? 'border-black dark:border-white bg-gray-50 dark:bg-neutral-800'
                            : 'border-gray-200 dark:border-neutral-800 hover:bg-gray-50 dark:hover:bg-neutral-800/50'
                        }`}
                        data-testid={`auto-topup-option-${p.packageId}`}
                      >
                        <div>
                          <p className="font-bold text-gray-900 dark:text-neutral-100 text-sm">
                            {p.type === 'unlimited' ? 'Unlimited' : `${p.dataAmount} GB`}
                          </p>
                          <p className="text-xs text-gray-500 dark:text-neutral-500">{p.duration} days</p>
                        </div>
                        <div className="flex items-center gap-2">
                          <span className="font-black text-gray-900 dark:text-neutral-100 text-sm">A${p.price.toFixed(2)}</span>
                          {sel && <Check size={16} className="text-emerald-600" />}
                        </div>
                      </button>
                    );
                  })}
                </div>

                <p className="text-xs text-gray-500 dark:text-neutral-500 mb-3">We&apos;ll charge your saved card •••• {pm?.payment_method?.card_last4 || '••••'} when your data runs low.</p>
                <div className="flex gap-2">
                  <button
                    onClick={handleEnable}
                    disabled={saving || !selectedPkgId}
                    className="flex-1 px-4 py-3 bg-black text-white text-sm font-bold rounded-xl hover:bg-gray-800 disabled:opacity-50 flex items-center justify-center gap-2 dark:border dark:border-white"
                    data-testid={`auto-topup-confirm-${orderId}`}
                  >
                    {saving ? <Loader2 size={14} className="animate-spin" /> : <Check size={14} />}
                    Enable auto top-up
                  </button>
                </div>
              </>
            )}
          </div>
        </div>
      )}
    </div>
  );
};

export default AutoTopUpControl;
